import React, { useRef, useState } from "react";
import { Bold, Italic, List, ListOrdered, Eye, Edit2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

// Share formatting parser
export const parseFormattedText = (text: string) => {
  if (!text) return [];
  const lines = text.split("\n");
  return lines.map((line, lineIdx) => {
    const isBullet =
      line.trimStart().startsWith("- ") || line.trimStart().startsWith("* ");
    const isOrdered = /^\s*\d+\.\s+/.test(line);

    let cleanLine = line;
    let prefix = "";
    if (isBullet) {
      const match = line.match(/^(\s*)(-\s*|\*\s*)(.*)/);
      if (match) {
        prefix = "• ";
        cleanLine = match[3];
      }
    } else if (isOrdered) {
      const match = line.match(/^(\s*)(\d+\.\s*)(.*)/);
      if (match) {
        prefix = match[2];
        cleanLine = match[3];
      }
    }

    const parts: { text: string; bold?: boolean; italic?: boolean }[] = [];
    let currentIdx = 0;
    const regex = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g;
    let match;

    while ((match = regex.exec(cleanLine)) !== null) {
      if (match.index > currentIdx) {
        parts.push({ text: cleanLine.substring(currentIdx, match.index) });
      }
      if (match[2]) {
        parts.push({ text: match[2], bold: true });
      } else if (match[4]) {
        parts.push({ text: match[4], italic: true });
      }
      currentIdx = regex.lastIndex;
    }

    if (currentIdx < cleanLine.length) {
      parts.push({ text: cleanLine.substring(currentIdx) });
    }

    return {
      key: lineIdx,
      prefix,
      parts,
      isBullet,
      isOrdered,
    };
  });
};

export const FormattedTextView: React.FC<{
  text?: string;
  className?: string;
}> = ({ text, className }) => {
  if (!text) return null;
  const parsedLines = parseFormattedText(text);

  return (
    <div
      className={`space-y-1.5 text-sm text-muted-foreground ${className || ""}`}
    >
      {parsedLines.map((line) => {
        const isList = line.isBullet || line.isOrdered;
        return (
          <div
            key={line.key}
            className={`flex items-start ${isList ? "pl-4" : ""}`}
          >
            {isList && (
              <span className="mr-2 font-semibold text-primary select-none">
                {line.prefix}
              </span>
            )}
            <span className="flex-1 break-words whitespace-pre-wrap">
              {line.parts.map((part, partIdx) => (
                <span
                  key={partIdx}
                  className={`${part.bold ? "font-bold text-foreground" : ""} ${
                    part.italic ? "italic text-foreground" : ""
                  }`}
                >
                  {part.text}
                </span>
              ))}
            </span>
          </div>
        );
      })}
    </div>
  );
};

interface SimpleDescEditorProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
}

export const SimpleDescEditor: React.FC<SimpleDescEditorProps> = ({
  value,
  onChange,
  placeholder = "Write description...",
  className = "",
}) => {
  const [mode, setMode] = useState<"write" | "preview">("write");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertFormat = (tag: "bold" | "italic" | "bullet" | "number") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    let replacement = "";
    if (tag === "bold") {
      replacement = `**${selectedText || "bold text"}**`;
    } else if (tag === "italic") {
      replacement = `*${selectedText || "italic text"}*`;
    } else if (tag === "bullet") {
      replacement = `\n- ${selectedText || "list item"}`;
    } else if (tag === "number") {
      replacement = `\n1. ${selectedText || "list item"}`;
    }

    const newValue =
      text.substring(0, start) + replacement + text.substring(end);
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + (tag === "bullet" || tag === "number" ? 1 : 0),
        start + replacement.length,
      );
    }, 0);
  };

  return (
    <div
      className={`border border-border rounded-md bg-card overflow-hidden ${className}`}
    >
      {/* Editor Header / Toolbar */}
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="w-8 h-8 hover:bg-muted"
            title="Bold (**text**)"
            onClick={() => insertFormat("bold")}
            disabled={mode === "preview"}
          >
            <Bold className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="w-8 h-8 hover:bg-muted"
            title="Italic (*text*)"
            onClick={() => insertFormat("italic")}
            disabled={mode === "preview"}
          >
            <Italic className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="w-8 h-8 hover:bg-muted"
            title="Bullet list (- item)"
            onClick={() => insertFormat("bullet")}
            disabled={mode === "preview"}
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="w-8 h-8 hover:bg-muted"
            title="Numbered list (1. item)"
            onClick={() => insertFormat("number")}
            disabled={mode === "preview"}
          >
            <ListOrdered className="w-4 h-4" />
          </Button>
        </div>

        {/* Tab triggers */}
        <div className="flex border border-border rounded bg-muted/40 p-0.5">
          <button
            type="button"
            onClick={() => setMode("write")}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-sm transition-all ${
              mode === "write"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Edit2 className="w-3.5 h-3.5" />
            Write
          </button>
          <button
            type="button"
            onClick={() => setMode("preview")}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-sm transition-all ${
              mode === "preview"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </button>
        </div>
      </div>

      {/* Editor Content Area */}
      <div className="p-3">
        {mode === "write" ? (
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="min-h-[100px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 py-1 resize-none bg-transparent"
          />
        ) : (
          <div className="min-h-[100px] py-1">
            {value.trim() ? (
              <FormattedTextView text={value} />
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Nothing to preview
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
